import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Pencil, Trash2, Trophy, Target, Zap,
  Loader2, Save, X, Tag, Gamepad2, Monitor, Hash,
} from "lucide-react";

const API = `${getApiUrl()}/api`;

const CATEGORY_LABELS = {
  achievement: { label: "Achievements", icon: Trophy, color: "text-purple-400" },
  score: { label: "Score", icon: Zap, color: "text-amber-400" },
  completion: { label: "Completions", icon: Target, color: "text-green-400" },
  tag: { label: "Tag-Based", icon: Tag, color: "text-teal-400" },
};

const SCOPE_LABELS = {
  total: "Total",
  platform: "Per-Platform",
  system: "Per-System",
  tag: "Per-Tag",
};

export default function MasterDatabaseMilestones() {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState([]);

  // Form
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    category: "tag", scope: "tag", scopeValue: "", scopeLabel: "",
    targets: "", targetNames: {}, enabled: true,
  });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showNames, setShowNames] = useState(false);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/milestone-configs`);
      setConfigs(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tags`);
      setTags(res.data || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchConfigs(); fetchTags(); }, [fetchConfigs, fetchTags]);

  const openNew = () => {
    setEditing(null);
    setForm({ category: "tag", scope: "tag", scopeValue: "", scopeLabel: "", targets: "1, 10, 25, 50, 100, 250, 500", targetNames: {}, enabled: true });
    setShowNames(false);
    setShowForm(true);
  };

  const openEdit = (cfg) => {
    setEditing(cfg);
    setForm({
      category: cfg.category,
      scope: cfg.scope,
      scopeValue: cfg.scopeValue || "",
      scopeLabel: cfg.scopeLabel || "",
      targets: (cfg.targets || []).join(", "),
      targetNames: cfg.targetNames || {},
      enabled: cfg.enabled,
    });
    setShowNames(Object.keys(cfg.targetNames || {}).some(k => cfg.targetNames[k]));
    setShowForm(true);
  };

  const handleSave = async () => {
    const targets = form.targets.split(",").map(s => parseInt(s.trim())).filter(n => !isNaN(n) && n > 0);
    if (targets.length === 0) return toast.error("Add at least one target number");

    // Clean targetNames: only include keys that match actual targets
    const cleanedNames = {};
    for (const t of targets) {
      const key = String(t);
      if (form.targetNames[key]?.trim()) {
        cleanedNames[key] = form.targetNames[key].trim();
      }
    }

    setSaving(true);
    try {
      const payload = {
        category: form.category,
        scope: form.scope,
        scopeValue: form.scopeValue || null,
        scopeLabel: form.scopeLabel || form.scopeValue || null,
        targets,
        targetNames: cleanedNames,
        enabled: form.enabled,
      };
      if (editing) {
        await axios.put(`${API}/milestone-configs/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/milestone-configs`, payload);
      }
      toast.success(editing ? "Config updated" : "Config created");
      setShowForm(false);
      setEditing(null);
      fetchConfigs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/milestone-configs/${deleteTarget.id}`);
      toast.success("Config deleted");
      setDeleteTarget(null);
      fetchConfigs();
    } catch (err) { toast.error("Failed to delete"); }
  };

  const handleToggle = async (cfg) => {
    try {
      await axios.put(`${API}/milestone-configs/${cfg.id}`, { enabled: !cfg.enabled });
      fetchConfigs();
    } catch (err) { toast.error("Failed to toggle"); }
  };

  const grouped = {};
  for (const cfg of configs) {
    const key = cfg.category;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(cfg);
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/app/master-database")} data-testid="back-to-mdb">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">Milestones</h1>
          <p className="text-white/50 text-sm">
            Configure milestone thresholds and add tag-based milestones. {configs.length} configs.
          </p>
        </div>
        <Button size="sm" onClick={openNew} className="bg-teal-600 hover:bg-teal-700" data-testid="add-milestone-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Milestone
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-white/30" /></div>
      ) : (
        <div className="space-y-6">
          {Object.entries(CATEGORY_LABELS).map(([catKey, catInfo]) => {
            const items = grouped[catKey];
            if (!items || items.length === 0) return null;
            const Icon = catInfo.icon;
            return (
              <Card key={catKey} className="bg-[#0A0A0A] border-white/10">
                <CardHeader className="py-3">
                  <CardTitle className={`text-base flex items-center gap-2 ${catInfo.color}`}>
                    <Icon className="w-4 h-4" />
                    {catInfo.label} Milestones
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {items.map(cfg => (
                    <div key={cfg.id} className={`p-3 rounded-lg border transition-all ${cfg.enabled ? "border-white/10 bg-white/5" : "border-white/5 bg-white/[0.02] opacity-50"}`} data-testid={`milestone-cfg-${cfg.id}`}>
                      <div className="flex items-center gap-3">
                        <Switch checked={cfg.enabled} onCheckedChange={() => handleToggle(cfg)} data-testid={`toggle-${cfg.id}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium text-sm">
                              {SCOPE_LABELS[cfg.scope] || cfg.scope}
                              {cfg.scopeLabel && cfg.scope !== "total" ? `: ${cfg.scopeLabel}` : ""}
                            </span>
                            {cfg.scope === "tag" && cfg.scopeValue && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] bg-teal-500/20 text-teal-400 border border-teal-500/30">
                                {tags.find(t => t.id === cfg.scopeValue)?.name || cfg.scopeLabel || cfg.scopeValue}
                              </span>
                            )}
                          </div>
                          <p className="text-white/40 text-xs mt-0.5">
                            Targets: {(cfg.targets || []).map(t => {
                              const name = (cfg.targetNames || {})[String(t)];
                              return name ? `${t.toLocaleString()} (${name})` : t.toLocaleString();
                            }).join(", ")}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-white/40 hover:text-white" onClick={() => openEdit(cfg)} data-testid={`edit-cfg-${cfg.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400/50 hover:text-red-400" onClick={() => setDeleteTarget(cfg)} data-testid={`delete-cfg-${cfg.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); setEditing(null); } }}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md" data-testid="milestone-form">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit" : "New"} Milestone Config</DialogTitle>
            <DialogDescription className="text-white/50">
              {editing ? "Update the milestone configuration." : "Create a new milestone tracker."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-white/60 mb-1 block">Category</label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v, scope: v === "tag" ? "tag" : form.scope })}>
                <SelectTrigger className="bg-zinc-900 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="achievement">Achievement Count</SelectItem>
                  <SelectItem value="score">Score</SelectItem>
                  <SelectItem value="completion">Completions</SelectItem>
                  <SelectItem value="tag">Tag-Based</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm text-white/60 mb-1 block">Scope</label>
              <Select value={form.scope} onValueChange={v => setForm({ ...form, scope: v })}>
                <SelectTrigger className="bg-zinc-900 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="total">Total (Global)</SelectItem>
                  <SelectItem value="platform">Per-Platform</SelectItem>
                  <SelectItem value="system">Per-System</SelectItem>
                  {form.category === "tag" && <SelectItem value="tag">Per-Tag</SelectItem>}
                </SelectContent>
              </Select>
            </div>

            {form.scope === "tag" && (
              <div>
                <label className="text-sm text-white/60 mb-1 block">Achievement Tag</label>
                <Select value={form.scopeValue} onValueChange={v => {
                  const tag = tags.find(t => t.id === v);
                  setForm({ ...form, scopeValue: v, scopeLabel: tag?.name || v });
                }}>
                  <SelectTrigger className="bg-zinc-900 border-white/10"><SelectValue placeholder="Select a tag..." /></SelectTrigger>
                  <SelectContent>
                    {tags.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {(form.scope === "platform" || form.scope === "system") && (
              <div>
                <label className="text-sm text-white/60 mb-1 block">
                  {form.scope === "platform" ? "Platform (leave blank for all)" : "System (leave blank for all)"}
                </label>
                <Input
                  value={form.scopeValue}
                  onChange={e => setForm({ ...form, scopeValue: e.target.value, scopeLabel: e.target.value })}
                  placeholder={form.scope === "platform" ? "e.g., xbox" : "e.g., Xbox One"}
                  className="bg-zinc-900 border-white/10"
                />
              </div>
            )}

            <div>
              <label className="text-sm text-white/60 mb-1 block">Targets (comma-separated numbers)</label>
              <Input
                value={form.targets}
                onChange={e => setForm({ ...form, targets: e.target.value })}
                placeholder="1, 10, 25, 50, 100, 250, 500"
                className="bg-zinc-900 border-white/10"
                data-testid="targets-input"
              />
              <p className="text-white/30 text-xs mt-1">
                Milestones trigger when the count reaches each target.
              </p>
            </div>

            {/* Target Names */}
            {(() => {
              const parsedTargets = form.targets.split(",").map(s => parseInt(s.trim())).filter(n => !isNaN(n) && n > 0);
              if (parsedTargets.length === 0) return null;
              return (
                <div>
                  <button
                    type="button"
                    className="text-sm text-teal-400 hover:text-teal-300 flex items-center gap-1 mb-2"
                    onClick={() => setShowNames(!showNames)}
                    data-testid="toggle-target-names"
                  >
                    <Hash className="w-3 h-3" />
                    {showNames ? "Hide" : "Name"} milestones
                  </button>
                  {showNames && (
                    <div className="space-y-2 max-h-48 overflow-y-auto pr-1" data-testid="target-names-section">
                      {parsedTargets.sort((a, b) => a - b).map(t => (
                        <div key={t} className="flex items-center gap-2">
                          <span className="text-white/50 text-xs w-16 text-right shrink-0 font-mono">{t.toLocaleString()}</span>
                          <Input
                            value={form.targetNames[String(t)] || ""}
                            onChange={e => setForm({
                              ...form,
                              targetNames: { ...form.targetNames, [String(t)]: e.target.value },
                            })}
                            placeholder={`Name for ${t.toLocaleString()}...`}
                            className="bg-zinc-900 border-white/10 h-7 text-xs"
                            data-testid={`target-name-${t}`}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="flex items-center gap-2">
              <Switch checked={form.enabled} onCheckedChange={v => setForm({ ...form, enabled: v })} />
              <span className="text-sm text-white/60">Enabled</span>
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-milestone-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Milestone Config</DialogTitle>
            <DialogDescription className="text-white/50">
              This will remove the milestone tracker. Existing milestone data won't be affected.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-milestone">
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
